import { supabase } from "@/integrations/supabase/client";

export type CelebrationLinkedOrder = {
  id: string;
  status: string;
  payment_status: string | null;
};

/** Block skip when a paid or processing gift is still tied to this celebration. */
export function getCelebrationSkipBlockReason(
  orders: CelebrationLinkedOrder[],
): string | null {
  for (const order of orders) {
    if (order.status === "cancelled") continue;
    const pay = (order.payment_status ?? "unpaid").toLowerCase();
    if (pay === "paid" || pay === "processing") {
      return "This celebration has a gift already in progress. Open Orders to manage it before skipping.";
    }
    if (order.status === "delivered" && pay !== "paid" && pay !== "refunded") {
      return "This celebration has a delivery with a payment issue. Open Orders or contact Lembra before skipping.";
    }
  }
  return null;
}

/** Unpaid, non-terminal orders we void when the buyer skips. */
export function isUnpaidCancellableOrder(order: CelebrationLinkedOrder): boolean {
  if (order.status === "cancelled" || order.status === "delivered") return false;
  const pay = (order.payment_status ?? "unpaid").toLowerCase();
  return pay !== "paid" && pay !== "processing" && pay !== "refunded";
}

export async function fetchCelebrationLinkedOrders(
  celebrationId: string,
): Promise<CelebrationLinkedOrder[]> {
  const { data, error } = await supabase
    .from("orders")
    .select("id, status, payment_status")
    .eq("celebration_id", celebrationId)
    .neq("status", "cancelled")
    .neq("status", "delivered");

  if (error) throw error;
  return (data as CelebrationLinkedOrder[] | null) ?? [];
}

export async function assertCelebrationCanBeSkipped(celebrationId: string): Promise<void> {
  const orders = await fetchCelebrationLinkedOrders(celebrationId);
  const reason = getCelebrationSkipBlockReason(orders);
  if (reason) throw new Error(reason);
}

/**
 * Cancel unpaid checkout orders tied to a celebration when the buyer skips
 * or otherwise abandons the send flow. Keeps /orders "waiting on you" in
 * sync with Home/Calendar skip actions.
 */
export async function cancelOpenOrdersForCelebration(
  celebrationId: string,
): Promise<{ cancelled: number }> {
  const orders = await fetchCelebrationLinkedOrders(celebrationId);
  const blockReason = getCelebrationSkipBlockReason(orders);
  if (blockReason) throw new Error(blockReason);

  let cancelled = 0;
  for (const order of orders) {
    if (!isUnpaidCancellableOrder(order)) continue;

    const { error: updErr } = await supabase
      .from("orders")
      .update({
        status: "cancelled",
        notes: "Cancelled — celebration skipped",
        updated_at: new Date().toISOString(),
      })
      .eq("id", order.id);

    if (!updErr) cancelled += 1;
  }

  return { cancelled };
}
